import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const CreateModuleModal = ({ onClose, onSubmit, projectId }) => {
  const [formData, setFormData] = useState({
    productionValueUsd: '',
    moduleUid: generateModuleUID(),
    moduleName: '',
    category: '',
    areaUse: '',
    quantity: 1,
    dimensionLength: '',
    dimensionWidth: '',
    dimensionHeight: '',
    weight: '',
    dryWetUnit: '', // Changed from boolean to string to store 'dry' or 'wet'
    wallSystems: false,
    fenestration: false,
    cladding: false,
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Generate unique module UID
  function generateModuleUID() {
    const timestamp = Date.now();
    const randomStr = Math.random()?.toString(36)?.substr(2, 5)?.toUpperCase();
    return `MOD-${timestamp}-${randomStr}`;
  }

  const categoryOptions = [
    { value: 'toilet-pod', label: 'Toilet Pod' },
    { value: 'kitchen-pod', label: 'Kitchen Pod' },
    { value: 'bathroom-pod', label: 'Bathroom Pod' },
    { value: 'utility-pod', label: 'Utility Pod' },
    { value: 'custom', label: 'Custom Module' }
  ];

  const areaUseOptions = [
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'industrial', label: 'Industrial' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'education', label: 'Education' },
    { value: 'hospitality', label: 'Hospitality' }
  ];

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors?.[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleCopyUID = () => {
    navigator?.clipboard?.writeText(formData?.moduleUid);
  };

  const handleRegenerateUID = () => {
    setFormData({ ...formData, moduleUid: generateModuleUID() });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.productionValueUsd || formData?.productionValueUsd < 0) newErrors.productionValueUsd = 'Production value is required';
    if (!formData?.moduleName?.trim()) newErrors.moduleName = 'Module name is required';
    if (!formData?.category) newErrors.category = 'Category is required';
    if (!formData?.areaUse) newErrors.areaUse = 'Area use is required';
    if (!formData?.quantity || formData?.quantity < 1) newErrors.quantity = 'Quantity must be at least 1';
    if (!formData?.dimensionLength || formData?.dimensionLength <= 0) newErrors.dimensionLength = 'Length is required';
    if (!formData?.dimensionWidth || formData?.dimensionWidth <= 0) newErrors.dimensionWidth = 'Width is required';
    if (!formData?.dimensionHeight || formData?.dimensionHeight <= 0) newErrors.dimensionHeight = 'Height is required';
    if (!formData?.weight || formData?.weight <= 0) newErrors.weight = 'Weight is required';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    await onSubmit({
      projectId,
      sku: formData?.moduleUid,
      productName: formData?.moduleName,
      description: formData?.description,
      category: formData?.category,
      areaUse: formData?.areaUse,
      quantity: formData?.quantity,
      productionValueUsd: parseFloat(formData?.productionValueUsd),
      dimensionLength: parseFloat(formData?.dimensionLength),
      dimensionWidth: parseFloat(formData?.dimensionWidth),
      dimensionHeight: parseFloat(formData?.dimensionHeight),
      weight: parseFloat(formData?.weight),
      dryWetUnit: formData?.dryWetUnit,
      wallSystems: formData?.wallSystems,
      fenestration: formData?.fenestration,
      cladding: formData?.cladding,
      allocated: 0,
      completed: 0,
      status: 'pending'
    });
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Package" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Create New Module</h2>
              <p className="text-sm text-muted-foreground">Define module specifications and configuration</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-5">
            {/* Two-column grid for efficient layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Production Value USD */}
              <Input
                label="Production Value USD"
                type="number"
                placeholder="Enter production value"
                value={formData?.productionValueUsd}
                onChange={(e) => handleInputChange('productionValueUsd', e?.target?.value)}
                error={errors?.productionValueUsd}
                required
                min="0"
                step="0.01"
              />

              {/* Module UID (Auto-generated, Read-only) */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Module UID <span className="text-destructive">*</span></label>
                <div className="flex gap-2">
                  <Input
                    value={formData?.moduleUid}
                    readOnly
                    className="flex-1 bg-muted cursor-not-allowed"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopyUID}
                    title="Copy UID"
                  >
                    <Icon name="Copy" size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleRegenerateUID}
                    title="Regenerate UID"
                  >
                    <Icon name="RefreshCw" size={16} />
                  </Button>
                </div>
              </div>

              {/* Module Name */}
              <Input
                label="Module Name"
                placeholder="Enter module name"
                value={formData?.moduleName}
                onChange={(e) => handleInputChange('moduleName', e?.target?.value)}
                error={errors?.moduleName}
                required
              />

              {/* Category */}
              <Select
                label="Category"
                placeholder="Select module category"
                options={categoryOptions}
                value={formData?.category}
                onChange={(value) => handleInputChange('category', value)}
                error={errors?.category}
                required
              />

              {/* Area Use */}
              <Select
                label="Area of Use"
                placeholder="Select area use"
                options={areaUseOptions}
                value={formData?.areaUse}
                onChange={(value) => handleInputChange('areaUse', value)}
                error={errors?.areaUse}
                required
              />

              {/* Quantity */}
              <Input
                label="Quantity"
                type="number"
                placeholder="Enter quantity"
                value={formData?.quantity}
                onChange={(e) => handleInputChange('quantity', parseInt(e?.target?.value) || 1)}
                error={errors?.quantity}
                required
                min="1"
              />
            </div>

            {/* Module Dimensions (L x W x H in MM) */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Module Dimensions (MM) <span className="text-destructive">*</span></label>
              <div className="grid grid-cols-3 gap-3">
                <Input
                  placeholder="Length"
                  type="number"
                  value={formData?.dimensionLength}
                  onChange={(e) => handleInputChange('dimensionLength', e?.target?.value)}
                  error={errors?.dimensionLength}
                  required
                  min="1"
                  step="0.01"
                />
                <Input
                  placeholder="Width"
                  type="number"
                  value={formData?.dimensionWidth}
                  onChange={(e) => handleInputChange('dimensionWidth', e?.target?.value)}
                  error={errors?.dimensionWidth}
                  required
                  min="1"
                  step="0.01"
                />
                <Input
                  placeholder="Height"
                  type="number"
                  value={formData?.dimensionHeight}
                  onChange={(e) => handleInputChange('dimensionHeight', e?.target?.value)}
                  error={errors?.dimensionHeight}
                  required
                  min="1"
                  step="0.01"
                />
              </div>
            </div>

            {/* Weight in Kg */}
            <Input
              label="Weight (Kg)"
              type="number"
              placeholder="Enter weight in kilograms"
              value={formData?.weight}
              onChange={(e) => handleInputChange('weight', e?.target?.value)}
              error={errors?.weight}
              required
              min="0.01"
              step="0.01"
            />

            {/* Checkboxes Section */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Module Features</label>
              <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                {/* Dry/Wet Unit Radio Buttons */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Dry or Wet Unit</label>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="dryWetUnit"
                        value="dry"
                        checked={formData?.dryWetUnit === 'dry'}
                        onChange={(e) => handleInputChange('dryWetUnit', e?.target?.value)}
                        className="w-4 h-4 text-primary border-input focus:ring-2 focus:ring-ring cursor-pointer"
                      />
                      <span className="text-sm text-foreground">Dry</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="dryWetUnit"
                        value="wet"
                        checked={formData?.dryWetUnit === 'wet'}
                        onChange={(e) => handleInputChange('dryWetUnit', e?.target?.value)}
                        className="w-4 h-4 text-primary border-input focus:ring-2 focus:ring-ring cursor-pointer"
                      />
                      <span className="text-sm text-foreground">Wet</span>
                    </label>
                  </div>
                </div>
                
                {/* Other Checkboxes */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <Checkbox
                    label="Wall Systems"
                    checked={formData?.wallSystems}
                    onChange={(checked) => handleInputChange('wallSystems', checked)}
                  />
                  <Checkbox
                    label="Fenestration"
                    checked={formData?.fenestration}
                    onChange={(checked) => handleInputChange('fenestration', checked)}
                  />
                  <Checkbox
                    label="Cladding"
                    checked={formData?.cladding}
                    onChange={(checked) => handleInputChange('cladding', checked)}
                  />
                </div>
              </div>
            </div>

            {/* Description (Optional) */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Description (Optional)</label>
              <textarea
                placeholder="Add additional notes or specifications"
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                className="w-full h-20 px-3 py-2 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
        </form>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/30">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitting}
            iconName="Plus"
          >
            Create Module
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateModuleModal;